// Off-Site SEO Management Component
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, ExternalLink, Plus, Play, Pause, CheckCircle, XCircle, AlertCircle, TrendingUp, Sparkles, Edit, Trash2, Settings } from 'lucide-react';

export function OffSiteSEO() {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Stats
  const [backlinkStats, setBacklinkStats] = useState(null);
  const [prStats, setPRStats] = useState(null);
  const [directoryStats, setDirectoryStats] = useState(null);
  
  // Backlinks
  const [campaigns, setCampaigns] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  
  // Press Releases
  const [pressReleases, setPressReleases] = useState([]);
  const [prSites, setPRSites] = useState([]);
  const [editingPR, setEditingPR] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [prFrequency, setPRFrequency] = useState(30);
  
  // Directories
  const [directories, setDirectories] = useState([]);
  const [directorySubmissions, setDirectorySubmissions] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(null);
  
  // New Campaign Modal
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    target_url: '',
    anchor_text: '',
    total_links: 50,
    links_per_day: 2
  });

  useEffect(() => {
    loadAllData();
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const settings = await api.request('/seo/settings');
      setPRFrequency(settings.pr_frequency_days || 30);
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  }

  async function loadAllData() {
    try {
      setLoading(true);
      await Promise.all([
        loadBacklinkData(),
        loadPRData(),
        loadDirectoryData()
      ]);
    } catch (err) {
      console.error('Failed to load SEO data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadBacklinkData() {
    try {
      const [stats, campaignsData, submissionsData] = await Promise.all([
        api.request('/seo/backlinks/stats'),
        api.request('/seo/backlinks/campaigns'),
        api.request('/seo/backlinks/submissions')
      ]);
      setBacklinkStats(stats);
      setCampaigns(campaignsData.campaigns || []);
      setSubmissions(submissionsData.submissions || []);
    } catch (err) {
      console.error('Failed to load backlink data:', err);
    }
  }

  async function loadPRData() {
    try {
      const [stats, prs, sites] = await Promise.all([
        api.request('/seo/press-releases/stats'),
        api.request('/seo/press-releases/'),
        api.request('/seo/press-releases/sites')
      ]);
      setPRStats(stats);
      setPressReleases(prs.press_releases || []);
      setPRSites(sites.sites || []);
    } catch (err) {
      console.error('Failed to load PR data:', err);
    }
  }

  async function loadDirectoryData() {
    try {
      const [stats, listings, subs] = await Promise.all([
        api.request('/seo/directories/stats'),
        api.request('/seo/directories/listings'),
        api.request('/seo/directories/submissions')
      ]);
      setDirectoryStats(stats);
      setDirectories(listings.listings || []);
      setDirectorySubmissions(subs.submissions || []);
    } catch (err) {
      console.error('Failed to load directory data:', err);
    }
  }

  async function handleGeneratePR(topicIndex) {
    if (!confirm('Generate a press release using AI? This will use your AI credits.')) return;
    
    try {
      await api.request('/seo/press-releases/generate', {
        method: 'POST',
        body: JSON.stringify({ topic_index: topicIndex })
      });
      alert('Press release generated successfully!');
      await loadPRData();
    } catch (err) {
      alert('Failed to generate PR: ' + err.message);
    }
  }

  async function handleDistributePR(prId) {
    if (!confirm('Distribute this press release to all active sites?')) return;
    
    try {
      await api.request(`/seo/press-releases/distribute/${prId}`, {
        method: 'POST'
      });
      alert('Press release distributed successfully!');
      await loadPRData();
    } catch (err) {
      alert('Failed to distribute PR: ' + err.message);
    }
  }

  async function handleEditPR(pr) {
    setEditingPR(pr);
  }

  async function handleSaveEditPR() {
    if (!editingPR) return;
    
    try {
      await api.request(`/seo/press-releases/${editingPR.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: editingPR.title,
          content: editingPR.content,
          summary: editingPR.summary
        })
      });
      alert('Press release updated successfully!');
      setEditingPR(null);
      await loadPRData();
    } catch (err) {
      alert('Failed to update PR: ' + err.message);
    }
  }

  async function handleDeletePR(prId) {
    if (!confirm('Are you sure you want to delete this press release? This will remove it from the public website.')) return;
    
    try {
      await api.request(`/seo/press-releases/${prId}`, {
        method: 'DELETE'
      });
      alert('Press release deleted successfully!');
      await loadPRData();
    } catch (err) {
      alert('Failed to delete PR: ' + err.message);
    }
  }

  async function handleSaveSettings() {
    try {
      await api.request('/seo/settings', {
        method: 'POST',
        body: JSON.stringify({
          pr_frequency_days: parseInt(prFrequency)
        })
      });
      alert('Settings saved! PRs will be auto-published every ' + prFrequency + ' days.');
      setShowSettings(false);
    } catch (err) {
      alert('Failed to save settings: ' + err.message);
    }
  }

  async function handleExecuteCampaign(campaignId) {
    if (!confirm('Execute backlink campaign? This will create new submissions.')) return;
    
    try {
      await api.request(`/seo/backlinks/execute-campaign/${campaignId}`, {
        method: 'POST'
      });
      alert('Campaign executed successfully!');
      await loadBacklinkData();
    } catch (err) {
      alert('Failed to execute campaign: ' + err.message);
    }
  }

  async function handleBulkSubmitDirectories(priority) {
    const description = prompt('Enter a description for your business (e.g., "AI-powered writing platform for authors"):');
    if (!description) return;
    
    setBulkSubmitting(priority);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        priority: priority,
        description: description,
        category: 'Writing Software'
      });
      
      const result = await api.request(`/seo/directories/bulk-submit?${params.toString()}`, {
        method: 'POST'
      });
      alert(`✅ Created ${result.submissions.length} directory submissions!\nSkipped ${result.skipped_count} already submitted.`);
      await loadDirectoryData();
    } catch (err) {
      alert('❌ Failed to submit: ' + err.message);
    } finally {
      setBulkSubmitting(null);
    }
  }

  function getStatusBadge(status) {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-blue-100 text-blue-800',
      pending: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      verified: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800',
      distributed: 'bg-green-100 text-green-800',
      manual_required: 'bg-orange-100 text-orange-800'
    };
    
    return (
      <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Backlink Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{backlinkStats?.campaigns?.active || 0}</div>
              <div className="text-xs text-gray-500">
                {backlinkStats?.submissions?.verified || 0} verified / {backlinkStats?.submissions?.total || 0} total
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Press Releases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{prStats?.press_releases?.distributed || 0}</div>
              <div className="text-xs text-gray-500">
                {prStats?.submissions?.approved || 0} approved / {prStats?.submissions?.total || 0} submissions
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Directory Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="text-2xl font-bold">{directoryStats?.submissions?.approved || 0}</div>
              <div className="text-xs text-gray-500">
                {directoryStats?.directories?.active || 0} active directories
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sub Tabs */}
      <div className="flex overflow-x-auto space-x-2 border-b border-gray-200">
        <button
          onClick={() => setActiveSubTab('backlinks')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
            activeSubTab === 'backlinks'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Backlink Campaigns
        </button>
        <button
          onClick={() => setActiveSubTab('press-releases')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
            activeSubTab === 'press-releases'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Press Releases
        </button>
        <button
          onClick={() => setActiveSubTab('directories')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
            activeSubTab === 'directories'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Directory Submissions
        </button>
      </div>

      {/* Backlink Campaigns Tab */}
      {activeSubTab === 'backlinks' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Backlink Drip Campaigns</span>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    onClick={async () => {
                      if (!confirm('🤖 Auto-generate an SEO backlink campaign?\n\nThis will:\n• Use your top keywords\n• Create natural anchor text variations\n• Set optimal link velocity\n• Start building links automatically\n\nContinue?')) return;
                      
                      try {
                        const result = await api.request('/seo/backlinks/campaigns/auto-generate', {
                          method: 'POST'
                        });
                        alert(`✅ Campaign auto-generated!\n\n📝 ${result.campaign.name}\n🔗 ${result.campaign.total_links_target} links @ ${result.campaign.links_per_day}/day\n⏱️ Duration: ~${Math.ceil(result.campaign.total_links_target / result.campaign.links_per_day)} days`);
                        loadBacklinkData();
                      } catch (err) {
                        alert('❌ Auto-generation failed: ' + err.message);
                      }
                    }}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Auto-Generate Campaign
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setCampaignForm({
                        name: '',
                        target_url: 'https://upost.com',
                        anchor_text: '',
                        total_links: 50,
                        links_per_day: 2
                      });
                      setShowNewCampaign(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Manual Setup
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No campaigns yet. Create your first automated backlink campaign!
                </div>
              ) : (
                <div className="space-y-4">
                  {campaigns.map((campaign) => (
                    <div key={campaign.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{campaign.name}</h4>
                          <p className="text-sm text-gray-500">{campaign.target_url}</p>
                        </div>
                        {getStatusBadge(campaign.status)}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Progress:</span>
                          <div className="font-medium">{campaign.links_created}/{campaign.total_links_target}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Links/Day:</span>
                          <div className="font-medium">{campaign.links_per_day}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Verified:</span>
                          <div className="font-medium">{campaign.verified_submissions || 0}</div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleExecuteCampaign(campaign.id)}
                          disabled={campaign.status !== 'active'}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Execute Now
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {submissions.slice(0, 10).map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between text-sm border-b pb-2">
                    <div>
                      <div className="font-medium">{sub.source_name}</div>
                      <div className="text-xs text-gray-500">{sub.target_url}</div>
                    </div>
                    {getStatusBadge(sub.status)}
                  </div>
                ))}
                {submissions.length === 0 && (
                  <div className="text-center py-4 text-gray-500">No submissions yet</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Press Releases Tab */}
      {activeSubTab === 'press-releases' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Automation Settings</span>
                <Button size="sm" variant="outline" onClick={() => setShowSettings(!showSettings)}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configure
                </Button>
              </CardTitle>
            </CardHeader>
            {showSettings && (
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Auto-Publish Frequency
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="number"
                        min="1"
                        max="90"
                        value={prFrequency}
                        onChange={(e) => setPRFrequency(e.target.value)}
                        className="border rounded px-3 py-2 w-24"
                      />
                      <span className="text-sm text-gray-600">days</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Press releases will be automatically generated and published every {prFrequency} days.
                    </p>
                  </div>
                  <Button onClick={handleSaveSettings}>
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Manual Generation (Optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Or manually generate from pre-defined topics:
              </p>
              <div className="space-y-2">
                {prStats?.topics?.map((topic, index) => (
                  <div key={index} className="flex items-center justify-between border rounded-lg p-3">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{topic}</div>
                      <div className="text-xs text-gray-500">Topic #{index + 1}</div>
                    </div>
                    <Button size="sm" onClick={() => handleGeneratePR(index)}>
                      <Sparkles className="w-4 h-4 mr-1" />
                      Generate
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Press Releases</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pressReleases.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No press releases yet. Generate one above!
                  </div>
                ) : (
                  pressReleases.map((pr) => (
                    <div key={pr.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium">{pr.title}</h4>
                          <p className="text-sm text-gray-500 line-clamp-2">{pr.summary}</p>
                        </div>
                        {getStatusBadge(pr.status)}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Submissions: {pr.approved_submissions || 0}/{pr.total_submissions || 0}</span>
                        {pr.auto_generated && <Badge variant="outline">AI Generated</Badge>}
                        {pr.status === 'distributed' && (
                          <a 
                            href={`/press/${pr.id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:underline flex items-center"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            View Public
                          </a>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        {pr.status === 'draft' && (
                          <Button 
                            size="sm" 
                            onClick={() => handleDistributePR(pr.id)}
                          >
                            <TrendingUp className="w-4 h-4 mr-1" />
                            Distribute
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditPR(pr)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeletePR(pr.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Directories Tab */}
      {activeSubTab === 'directories' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Directory Submission</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Submit UPost to directories by priority level:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button 
                  variant="outline" 
                  onClick={() => handleBulkSubmitDirectories('high')}
                  disabled={bulkSubmitting === 'high'}
                  className="h-auto py-4 flex flex-col items-start"
                >
                  {bulkSubmitting === 'high' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mb-1" />
                      <span className="font-semibold">Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">High Priority</span>
                      <span className="text-xs text-gray-500">
                        {directoryStats?.directories?.high_priority || 0} directories
                      </span>
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleBulkSubmitDirectories('medium')}
                  disabled={bulkSubmitting === 'medium'}
                  className="h-auto py-4 flex flex-col items-start"
                >
                  {bulkSubmitting === 'medium' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mb-1" />
                      <span className="font-semibold">Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">Medium Priority</span>
                      <span className="text-xs text-gray-500">
                        {directoryStats?.directories?.medium_priority || 0} directories
                      </span>
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleBulkSubmitDirectories('low')}
                  disabled={bulkSubmitting === 'low'}
                  className="h-auto py-4 flex flex-col items-start"
                >
                  {bulkSubmitting === 'low' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mb-1" />
                      <span className="font-semibold">Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">Low Priority</span>
                      <span className="text-xs text-gray-500">
                        {directoryStats?.directories?.low_priority || 0} directories
                      </span>
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Directory Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {directorySubmissions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No submissions yet. Use bulk submission above!
                  </div>
                ) : (
                  directorySubmissions.slice(0, 20).map((sub) => (
                    <div key={sub.id} className="flex items-center justify-between border-b pb-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{sub.directory_name}</div>
                        <div className="text-xs text-gray-500">{sub.description?.substring(0, 60)}...</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(sub.status)}
                        {sub.listing_url && (
                          <a href={sub.listing_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 text-gray-400" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit PR Modal */}
      {editingPR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Edit Press Release</h3>
                <button 
                  onClick={() => setEditingPR(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={editingPR.title}
                    onChange={(e) => setEditingPR({...editingPR, title: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Summary</label>
                  <textarea
                    value={editingPR.summary}
                    onChange={(e) => setEditingPR({...editingPR, summary: e.target.value})}
                    rows="3"
                    className="w-full border rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Full Content</label>
                  <textarea
                    value={editingPR.content}
                    onChange={(e) => setEditingPR({...editingPR, content: e.target.value})}
                    rows="15"
                    className="w-full border rounded px-3 py-2 font-mono text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditingPR(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEditPR}>
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Campaign Modal */}
      {showNewCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Create New Backlink Campaign</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewCampaign(false)}
                >
                  ✕
                </Button>
              </div>

              <div className="space-y-4">
                {/* Campaign Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Campaign Name</label>
                  <input
                    type="text"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                    className="w-full p-3 border rounded-lg"
                    placeholder="e.g., Homepage Backlinks Q1 2025"
                  />
                </div>

                {/* Target URL */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Target URL</label>
                  <input
                    type="url"
                    value={campaignForm.target_url}
                    onChange={(e) => setCampaignForm({ ...campaignForm, target_url: e.target.value })}
                    className="w-full p-3 border rounded-lg"
                    placeholder="https://yoursite.com/page"
                  />
                  <p className="text-xs text-gray-500 mt-1">The page you want to build backlinks to</p>
                </div>

                {/* Anchor Text */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Anchor Text</label>
                  <input
                    type="text"
                    value={campaignForm.anchor_text}
                    onChange={(e) => setCampaignForm({ ...campaignForm, anchor_text: e.target.value })}
                    className="w-full p-3 border rounded-lg"
                    placeholder="e.g., AI writing platform"
                  />
                  <p className="text-xs text-gray-500 mt-1">The clickable text for your backlinks</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Total Links */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Total Links Target</label>
                    <input
                      type="number"
                      value={campaignForm.total_links}
                      onChange={(e) => setCampaignForm({ ...campaignForm, total_links: parseInt(e.target.value) || 0 })}
                      className="w-full p-3 border rounded-lg"
                      min="1"
                      max="1000"
                    />
                  </div>

                  {/* Links Per Day */}
                  <div>
                    <label className="block text-sm font-semibold mb-2">Links Per Day</label>
                    <input
                      type="number"
                      value={campaignForm.links_per_day}
                      onChange={(e) => setCampaignForm({ ...campaignForm, links_per_day: parseInt(e.target.value) || 1 })}
                      className="w-full p-3 border rounded-lg"
                      min="1"
                      max="20"
                    />
                    <p className="text-xs text-gray-500 mt-1">Natural drip-feed rate</p>
                  </div>
                </div>

                {/* Campaign Info */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Campaign Duration:</strong> ~{Math.ceil(campaignForm.total_links / (campaignForm.links_per_day || 1))} days
                  </p>
                  <p className="text-xs text-blue-700 mt-2">
                    Links will be created gradually to appear natural to search engines. This "drip-feed" method is the safest approach for SEO.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={async () => {
                      if (!campaignForm.name || !campaignForm.target_url || !campaignForm.anchor_text) {
                        alert('Please fill in all required fields');
                        return;
                      }
                      
                      try {
                        await api.request('/seo/backlinks/campaigns', {
                          method: 'POST',
                          body: JSON.stringify({
                            name: campaignForm.name,
                            target_url: campaignForm.target_url,
                            anchor_text: campaignForm.anchor_text,
                            total_links_target: campaignForm.total_links,
                            links_per_day: campaignForm.links_per_day,
                            status: 'active'
                          })
                        });
                        alert('✅ Campaign created successfully!');
                        setShowNewCampaign(false);
                        loadBacklinkData();
                      } catch (err) {
                        alert('❌ Failed to create campaign: ' + err.message);
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Create & Start Campaign
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowNewCampaign(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
